import React, { useMemo, useState } from 'react';
import { BookOutlined } from '@ant-design/icons';
import { Drawer, Input, Select, Space, Typography, message } from 'antd';
import * as api from '../api/client';
import KnowledgeBaseSelector from './KnowledgeBaseSelector';
import KnowledgeBaseCreateModal from './KnowledgeBaseCreateModal';

const { Text } = Typography;

export interface KnowledgeBaseEntryDraft {
  suggestedTitle: string;
  summary?: string;
  contentMarkdown?: string;
  sourceModule?: string;
  sourceId?: string;
  metadata?: Record<string, any>;
  tags?: string[];
  artifactIds?: string[];
}

interface Props {
  open: boolean;
  userId?: string;
  draft: KnowledgeBaseEntryDraft;
  onClose: () => void;
  onSaved?: (result: { knowledgeBaseId: string; knowledgeBaseName?: string }) => void;
}

const KnowledgeBaseEntryDrawer: React.FC<Props> = ({ open, onClose, draft, userId = '', onSaved }) => {
  const [kbId, setKbId] = useState('');
  const [title, setTitle] = useState(draft.suggestedTitle || '');
  const [summary, setSummary] = useState(draft.summary || '');
  const [contentMarkdown, setContentMarkdown] = useState(draft.contentMarkdown || '');
  const [tags, setTags] = useState<string[]>(draft.tags || []);
  const [artifactIds, setArtifactIds] = useState<string[]>(draft.artifactIds || []);
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const selectedArtifacts = useMemo(
    () => artifactIds.map((id) => ({ value: id, label: id })),
    [artifactIds],
  );

  const reset = () => {
    setKbId('');
    setTitle(draft.suggestedTitle || '');
    setSummary(draft.summary || '');
    setContentMarkdown(draft.contentMarkdown || '');
    setTags(draft.tags || []);
    setArtifactIds(draft.artifactIds || []);
  };

  const handleSave = async () => {
    if (!kbId) {
      message.warning('Please select a knowledge base');
      return;
    }
    if (!title.trim()) {
      message.warning('Title is required');
      return;
    }
    setSaving(true);
    try {
      await api.createKnowledgeBaseEntry({
        knowledge_base_id: kbId,
        title: title.trim(),
        summary: summary.trim(),
        content_markdown: contentMarkdown,
        content_json: {
          sections: [{ type: 'markdown', content: contentMarkdown }],
        },
        tags,
        source_module: draft.sourceModule || '',
        source_id: draft.sourceId || '',
        metadata: draft.metadata || {},
        artifact_ids: artifactIds,
      });
      let knowledgeBaseName: string | undefined;
      try {
        const kbRes = await api.getKnowledgeBase(kbId);
        knowledgeBaseName = kbRes?.data?.name;
      } catch {
        // Best-effort lookup only.
      }
      message.success('Added to knowledge base');
      onSaved?.({ knowledgeBaseId: kbId, knowledgeBaseName });
      onClose();
      reset();
    } catch {
      message.error('Failed to create knowledge base entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Drawer
        className="praxys-kb-entry-drawer"
        title={(
          <span className="praxys-kb-entry-drawer-title">
            <BookOutlined />
            Add to Knowledge Base
          </span>
        )}
        placement="right"
        width={560}
        open={open}
        onClose={() => { onClose(); reset(); }}
        closable
        closeIcon={<span aria-hidden>✕</span>}
        bodyStyle={{ padding: 0 }}
        maskClosable={false}
        keyboard={false}
      >
        <div className="praxys-kb-entry-drawer-content">
          <div className="praxys-kb-entry-drawer-body">
            <p className="praxys-kb-entry-drawer-subtitle">
              Save this result as reusable project knowledge.
            </p>
            <Space direction="vertical" style={{ width: '100%' }} size={14}>
              <div>
                <Text className="praxys-kb-entry-form-label">Knowledge Base</Text>
                <Space style={{ width: '100%' }} direction="vertical" size={8}>
                  <KnowledgeBaseSelector userId={userId} value={kbId} onChange={setKbId} />
                  <button
                    type="button"
                    onClick={() => setCreateOpen(true)}
                    className="praxys-secrets-drawer-btn praxys-secrets-drawer-btn--secondary praxys-kb-entry-inline-btn"
                  >
                    Create New Knowledge Base
                  </button>
                </Space>
              </div>
              <div>
                <Text className="praxys-kb-entry-form-label">Title</Text>
                <Input className="praxys-kb-entry-input" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div>
                <Text className="praxys-kb-entry-form-label">Summary</Text>
                <Input.TextArea className="praxys-kb-entry-input" value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} />
              </div>
              <div>
                <Text className="praxys-kb-entry-form-label">Content (Markdown)</Text>
                <Input.TextArea className="praxys-kb-entry-input" value={contentMarkdown} onChange={(e) => setContentMarkdown(e.target.value)} rows={10} />
              </div>
              <div>
                <Text className="praxys-kb-entry-form-label">Tags</Text>
                <Select
                  mode="tags"
                  className="praxys-kb-entry-select"
                  value={tags}
                  onChange={(next) => setTags(next)}
                  style={{ width: '100%' }}
                  placeholder="Add tags"
                />
              </div>
              <div>
                <Text className="praxys-kb-entry-form-label">Attached Artifacts</Text>
                <Select
                  mode="tags"
                  className="praxys-kb-entry-select"
                  value={artifactIds}
                  options={selectedArtifacts}
                  onChange={(next) => setArtifactIds(next)}
                  style={{ width: '100%' }}
                  placeholder="Artifact IDs"
                />
              </div>
            </Space>
          </div>
          <div className="praxys-kb-entry-drawer-footer">
            <button
              type="button"
              onClick={() => { onClose(); reset(); }}
              className="praxys-secrets-drawer-btn praxys-secrets-drawer-btn--secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="praxys-secrets-drawer-btn praxys-secrets-drawer-btn--primary"
            >
              {saving ? 'Saving…' : 'Save to KB'}
            </button>
          </div>
        </div>
      </Drawer>
      <KnowledgeBaseCreateModal
        open={createOpen}
        userId={userId}
        onCancel={() => setCreateOpen(false)}
        onCreated={(kb) => {
          setKbId(kb.id);
          setCreateOpen(false);
        }}
      />
    </>
  );
};

export default KnowledgeBaseEntryDrawer;
